'use client';

import * as React from 'react';
import { useLocation } from 'react-router-dom';

import { ResourceBadge } from '@/components/ui/resource-badge';
import { useOrgNavigate } from '@/hooks/use-app-navigate';

interface CustomerResourceBadgeProps {
  customerId: string;
  customerName?: string;
  variant?: 'default' | 'secondary' | 'destructive' | 'outline';
  className?: string;
}

/**
 * CustomerResourceBadge component for displaying customer information with navigation
 */
export function CustomerResourceBadge({
  customerId,
  customerName,
  variant = 'secondary',
  className,
}: CustomerResourceBadgeProps) {
  const { orgRoutes } = useOrgNavigate();
  const location = useLocation();

  // Use customer name if available, otherwise use truncated customer ID
  const displayValue = React.useMemo(() => {
    return customerName || `${customerId.substring(0, 8)}...`;
  }, [customerName, customerId]);

  // Construct the link with returnUrl
  const linkUrl = React.useMemo(() => {
    const baseUrl = orgRoutes.CUSTOMER_DETAILS(customerId);
    const currentUrl = `${location.pathname}${location.search}`;
    return `${baseUrl}?returnUrl=${encodeURIComponent(currentUrl)}`;
  }, [orgRoutes, customerId, location.pathname, location.search]);

  return (
    <ResourceBadge
      name="customer"
      value={displayValue}
      appLink={linkUrl}
      variant={variant}
      className={className}
    />
  );
}
